import type { AccountType } from '~/types/user';

/**
 * True when the visitor is on the companies.* portal host rather than www.
 *
 * After the Labs cutover companies.* is the only host wad-platform serves, and
 * it carries company + admin accounts only. Anything developer-facing (sign-up
 * entry points, GitHub login) has to be gated on this.
 *
 * Call from setup: useSelfBaseUrl reads request headers on the server, which is
 * only valid there. The host cannot change during a session, so a plain boolean
 * is enough - it resolves from x-forwarded-host during SSR and from the window
 * origin again on hydration, correct in both.
 */
export const useIsCompaniesHost = (): boolean =>
  new URL(useSelfBaseUrl()).hostname.startsWith('companies.');

/** The only account types the company portal serves. */
const PORTAL_ACCOUNT_TYPES: AccountType[] = ['company', 'admin'];

/**
 * Hands the blocked account's email across the gate's redirect to
 * /login?blocked=…, so the notice there can still name who arrived even though
 * the session it came from has just been ended.
 *
 * sessionStorage, not the URL: an email address in the query string would sit
 * in browser history and server logs. Not localStorage either (unlike
 * useOAuthReturn's path, which is not personal data): the address should not
 * outlive the tab or surface in a different one.
 */
const BLOCKED_EMAIL_KEY = 'portal:blocked-email';

export const rememberBlockedEmail = (email: string | undefined) => {
  if (!email) {
    return;
  }
  try {
    sessionStorage.setItem(BLOCKED_EMAIL_KEY, email);
  } catch {}
};

/**
 * Reads and clears in one step. The login page calls this on every mount, not
 * only when ?blocked is present, so an entry from an abandoned redirect cannot
 * linger and attach itself to a later, unrelated visit to the notice.
 */
export const consumeBlockedEmail = (): string | null => {
  try {
    const email = sessionStorage.getItem(BLOCKED_EMAIL_KEY);
    sessionStorage.removeItem(BLOCKED_EMAIL_KEY);
    return email;
  } catch {
    return null;
  }
};

type SessionAccount = { type: AccountType; home?: string };

/**
 * What the gate did, and therefore what the caller should do next.
 *
 * 'allow'   the account belongs here, carry on as before
 * 'handled' the gate navigated away, stop
 * 'failed'  something broke; the session has been ended and the caller owes the
 *           user an error message
 */
export type PortalGateOutcome = 'allow' | 'handled' | 'failed';

/**
 * Stops accounts that do not belong in the company portal from ending up inside
 * it after signing in on companies.*.
 */
export const usePortalAccessGate = () => {
  const isCompaniesHost = useIsCompaniesHost();
  const { signOut } = useAuth();
  const { $api } = useNuxtApp();

  /**
   * Call with a freshly resolved session, before navigating anywhere with it.
   * Never throws: a caller that only checked for an exception would leave the
   * user staring at a form that silently did nothing.
   */
  const enforce = async (session: any): Promise<PortalGateOutcome> => {
    if (!isCompaniesHost || !session?.data?.id) {
      return 'allow';
    }

    const accountType = session.data.account_type as AccountType | undefined;
    if (accountType && PORTAL_ACCOUNT_TYPES.includes(accountType)) {
      return 'allow';
    }

    // Company beats admin, which matches the bulk move in TAL-10776, because two different preferences
    // would pass the same user back and forth between the two.
    const accounts = (session.data.accounts ?? []) as SessionAccount[];
    const portalAccount =
      accounts.find((account) => account.type === 'company')
      ?? accounts.find((account) => account.type === 'admin');

    if (portalAccount) {
      try {
        await $api('/v2/user/accounts/default', {
          method: 'PUT',
          body: { account_type: portalAccount.type },
        });
      } catch {
        // Deliberately not falling through to the blocked message below: this
        // person holds a real portal account, and telling them their developer
        // identity moved to Labs would be untrue. Their default account is
        // untouched, so signing in again retries the switch. End the session so
        // they cannot wander into the dashboard on the account that failed.
        try {
          await signOut({ redirect: false });
        } catch {
          // The caller shows an error either way, and this account has no
          // access to anything in the portal.
        }
        return 'failed';
      }
      window.location.href = portalAccount.home || '/';
      return 'handled';
    }

    // Nothing in the portal belongs to this account. End the session.
    try {
      await signOut({ redirect: false });
    } catch {
      // Do not navigate. Landing back on /login while the session is somehow
      // still alive would retrigger the gate and bounce the user in a loop.
      return 'failed';
    }
    rememberBlockedEmail(session.data.email);
    window.location.href = `/login?blocked=${accountType === 'developer' ? 'labs' : 'app'}`;
    return 'handled';
  };

  return { enforce };
};
